import React from "react";
import { IconNames } from "@src/straps/base/icons/Icon/types";
import {
  ProjectBudgetStatus,
  ProjectStatus,
  ProjectTimelineStatus,
  ProjectType,
} from "@src/types";
import { Entries } from "type-fest";
import BudgetStatusNode from "./ProjectPage/BudgetStatusNode";
import TimelineStatusNode from "./ProjectPage/TimelineStatusNode";
import ProjectCategoryNode from "./ProjectPage/ProjectCategoryNode";
import ProjectStatusNode from "./ProjectPage/ProjectStatusNode";
import { Reward } from "@src/global_functions/backpackSdk/projects/rewards";
import RewardStatusNode from "./ProjectPage/RewardStatusNode";

export const projectStatusRenderingOptions: Record<
  ProjectStatus,
  {
    borderColorClass: string;
    iconColor: string; // Some of the required colors are not Semantic Colors, so setting icon color by setting the text color
    icon: IconNames;
    bgColor: string;
  }
> = {
  proposed: {
    borderColorClass: "border-[#CA7127]",
    iconColor: "text-[#CA7127]",
    icon: "proposed",
    bgColor: "bg-[#FCDDC4]",
  },
  planned: {
    borderColorClass: "border-charcoal500",
    iconColor: "text-charcoal500",
    icon: "planned",
    bgColor: "bg-straps-accent-3",
  },
  inProgress: {
    borderColorClass: "border-blue",
    iconColor: "text-blue",
    icon: "in-progress",
    bgColor: "bg-blue/25",
  },
  completed: {
    borderColorClass: "border-straps-positive",
    iconColor: "text-straps-positive",
    icon: "completed",
    bgColor: "bg-straps-positive-bg",
  },
};

export const statusOptions: Array<{
  id: ProjectStatus;
  nodeLabel: React.ReactNode;
}> = (
  Object.entries(projectStatusRenderingOptions) as Entries<
    typeof projectStatusRenderingOptions
  >
).map(([status]) => {
  return {
    id: status,
    nodeLabel: <ProjectStatusNode status={status} hasBackground={false} />,
  };
});

export const projectCategoryRenderingValues: Record<
  ProjectType,
  { color: string; title: string; bgColor: string } // bgColor only used for legacy projects and can be removed once the redesign is released
> = {
  hvac: { title: "HVAC", color: "text-blue", bgColor: "bg-blue" },
  repairs_and_maintenance: {
    title: "Repairs & Maintenance",
    color: "text-green-kelly",
    bgColor: "bg-green-kelly",
  },
  renovation: {
    title: "Renovation",
    color: "text-orange",
    bgColor: "bg-orange",
  },
  ev_and_solar: {
    title: "EV + Solar",
    color: "text-yellow",
    bgColor: "bg-yellow",
  },
};

export const categoryOptions: Array<{
  id: ProjectType;
  nodeLabel: React.ReactNode;
}> = (
  Object.entries(projectCategoryRenderingValues) as Entries<
    typeof projectCategoryRenderingValues
  >
).map(([category]) => {
  return {
    id: category,
    nodeLabel: <ProjectCategoryNode projectType={category} />,
  };
});

export const timelineStatusRenderingOptions: Record<
  ProjectTimelineStatus,
  { label: string; color: string; backgroundColor: string }
> = {
  on_time: {
    color: "text-straps-positive",
    label: "On Schedule",
    backgroundColor: "bg-straps-positive/25",
  },
  delayed: {
    color: "text-straps-negative",
    label: "Off Schedule",
    backgroundColor: "bg-straps-negative/25",
  },
  unknown: {
    color: "text-pure-white-300",
    label: "Unknown Timeline",
    backgroundColor: "bg-pure-white-300/25",
  },
};

export const timelineStatusOptions: Array<{
  id: ProjectTimelineStatus;
  nodeLabel: React.ReactNode;
}> = (
  Object.entries(timelineStatusRenderingOptions) as Entries<
    typeof timelineStatusRenderingOptions
  >
).map(([status]) => {
  return {
    id: status,
    nodeLabel: <TimelineStatusNode timelineStatus={status} />,
  };
});

export const budgetStatusRenderingOptions: Record<
  ProjectBudgetStatus,
  { label: string; color: string }
> = {
  within_budget: {
    color: "text-straps-positive",
    label: "On Budget",
  },
  over_budget: {
    color: "text-straps-negative",
    label: "Over Budget",
  },
  unknown: {
    color: "text-pure-white-300",
    label: "Unknown Budget",
  },
};

export const budgetStatusOptions: Array<{
  id: ProjectBudgetStatus;
  nodeLabel: React.ReactNode;
}> = (
  Object.entries(budgetStatusRenderingOptions) as Entries<
    typeof budgetStatusRenderingOptions
  >
).map(([status]) => {
  return {
    id: status,
    nodeLabel: <BudgetStatusNode budgetStatus={status} />,
  };
});

export const rewardStatusRenderingOptions: Record<
  Reward["status"],
  { label: string; color: string }
> = {
  processing: {
    color: "text-[#416CE9]",
    label: "Processing",
  },
  applied: {
    color: "text-straps-positive",
    label: "Applied",
  },
};

export const rewardStatusOptions: Array<{
  id: Reward["status"];
  nodeLabel: React.ReactNode;
}> = (
  Object.entries(rewardStatusRenderingOptions) as Entries<
    typeof rewardStatusRenderingOptions
  >
).map(([status]) => {
  return {
    id: status,
    nodeLabel: <RewardStatusNode rewardStatus={status} />,
  };
});

export const PROJECT_PATHS = {
  PORTFOLIO_PAGE: "/projects",
  BASE_SITE_PATH: "/site/:siteId/projects",
  SITE_PAGE: "/site/:siteId/projects/all-projects",
  DECARB_PAGE: "/site/:siteId/projects/decarb-planning",
  PROJECT_PAGE: "/site/:siteId/projects/all-projects/:projectId",
} as const;

export const isProjectType = (type: unknown): type is ProjectType => {
  return typeof type === "string" && type in projectCategoryRenderingValues;
};
